import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { 
  FileText, 
  Upload,
  MagnifyingGlass,
  ArrowClockwise,
  Sparkle,
  CheckCircle,
  Warning,
  CurrencyDollar,
  User,
  Calendar,
  X,
  Eye,
  Copy
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function EOBImportPage() {
  const [eobs, setEobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showParseModal, setShowParseModal] = useState(false);
  const [selectedEOB, setSelectedEOB] = useState(null);
  const [patients, setPatients] = useState([]);
  const [claims, setClaims] = useState([]);
  
  // Parse form state
  const [eobText, setEobText] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  
  // Import form state
  const [importData, setImportData] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchEOBs();
    fetchPatients();
    fetchClaims();
  }, []);

  const fetchEOBs = async () => {
    try {
      const response = await api.get('/billing/eobs');
      setEobs(response.data.eobs || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching EOBs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/admin/users?role=patient');
      const patientList = Array.isArray(response.data) ? response.data : (response.data.users || []);
      setPatients(patientList);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching patients:', error);
    }
  };

  const fetchClaims = async () => {
    try {
      const response = await api.get('/billing/claims?status=submitted');
      setClaims(response.data.claims || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching claims:', error);
    }
  };

  const parseEOB = async () => {
    if (!eobText.trim() || !selectedPatientId) {
      toast.error('Please enter EOB text and select a patient');
      return;
    }
    
    setParsing(true);
    try {
      const response = await api.post('/billing/eob/parse', {
        eob_text: eobText,
        patient_id: selectedPatientId
      });
      
      if (response.data.success) {
        setParsedData(response.data.parsed_eob);
        toast.success('EOB parsed successfully');
      } else {
        toast.error(response.data.error || 'Failed to parse EOB');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse EOB');
    } finally {
      setParsing(false);
    }
  };

  const importEOB = async (data) => {
    setImporting(true);
    try {
      const response = await api.post('/billing/eob/import', data);
      toast.success('EOB imported successfully');
      setShowParseModal(false);
      setShowImportModal(false);
      setParsedData(null);
      setEobText('');
      setSelectedPatientId('');
      setImportData(null);
      fetchEOBs();
      fetchClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import EOB');
    } finally {
      setImporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="p-8" data-testid="eob-import-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            EOB Import
          </h1>
          <p className="text-slate-500">Import and manage Explanation of Benefits documents</p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setShowParseModal(true)}
            className="btn-outline flex items-center gap-2"
          >
            <Sparkle size={18} />
            AI Parse EOB
          </button>
          <button
            onClick={() => {
              setImportData({
                patient_id: '',
                claim_id: '',
                insurance_name: '',
                policy_number: '',
                eob_date: new Date().toISOString().split('T')[0],
                check_number: '',
                check_date: '',
                provider_name: '',
                line_items: [],
                total_billed: 0,
                total_allowed: 0,
                total_paid: 0,
                total_patient_responsibility: 0,
                notes: ''
              });
              setShowImportModal(true);
            }}
            className="btn-teal flex items-center gap-2"
          >
            <Upload size={18} />
            Manual Import
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Total EOBs</p>
          <p className="text-2xl font-bold text-slate-900">{eobs.length}</p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Total Billed</p>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(eobs.reduce((sum, e) => sum + (e.totals?.billed || 0), 0))}
          </p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Insurance Paid</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(eobs.reduce((sum, e) => sum + (e.totals?.paid || 0), 0))}
          </p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Patient Responsibility</p>
          <p className="text-2xl font-bold text-amber-600">
            {formatCurrency(eobs.reduce((sum, e) => sum + (e.totals?.patient_responsibility || 0), 0))}
          </p>
        </div>
      </div>

      {/* EOB List */}
      <div className="card-clinical overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Patient</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Insurance</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">EOB Date</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Billed</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Allowed</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Paid</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Patient Owes</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="py-8 text-center text-slate-500">
                  <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                  Loading EOBs...
                </td>
              </tr>
            ) : eobs.length === 0 ? (
              <tr>
                <td colSpan="8" className="py-8 text-center text-slate-500">
                  <FileText size={48} className="mx-auto mb-2 text-slate-300" />
                  No EOBs imported yet
                </td>
              </tr>
            ) : (
              eobs.map((eob) => (
                <tr key={eob.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{eob.patient_name}</p>
                    <p className="text-xs text-slate-500">#{eob.policy_number}</p>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-700">{eob.insurance_name}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{formatDate(eob.eob_date)}</td>
                  <td className="py-3 px-4 text-right text-sm">{formatCurrency(eob.totals?.billed)}</td>
                  <td className="py-3 px-4 text-right text-sm">{formatCurrency(eob.totals?.allowed)}</td>
                  <td className="py-3 px-4 text-right text-sm text-green-600 font-medium">
                    {formatCurrency(eob.totals?.paid)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-amber-600 font-medium">
                    {formatCurrency(eob.totals?.patient_responsibility)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => setSelectedEOB(eob)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <Eye size={18} className="text-slate-500" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* AI Parse Modal */}
      {showParseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Sparkle size={24} className="text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">AI EOB Parser</h2>
                  <p className="text-sm text-slate-500">Paste EOB text to extract structured data</p>
                </div>
              </div>
              <button onClick={() => { setShowParseModal(false); setParsedData(null); }} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            {!parsedData ? (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Patient</label>
                  <select
                    value={selectedPatientId}
                    onChange={(e) => setSelectedPatientId(e.target.value)}
                    className="input-clinical"
                  >
                    <option value="">Choose a patient...</option>
                    {patients.map((p) => (
                      <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    EOB Text
                    <span className="text-slate-400 font-normal ml-2">(paste from PDF or image)</span>
                  </label>
                  <textarea
                    value={eobText}
                    onChange={(e) => setEobText(e.target.value)}
                    className="input-clinical font-mono text-sm"
                    rows={12}
                    placeholder={`Paste EOB content here...

Example:
EXPLANATION OF BENEFITS
Insurance: Blue Cross Blue Shield
Member ID: XYZ123456
Service Date: 01/15/2026
Provider: T-MEDCMA Healthcare

Procedure    Billed    Allowed    Paid    You Owe
99213        $150.00   $120.00    $96.00  $24.00
87880        $45.00    $40.00     $32.00  $8.00

Total:       $195.00   $160.00    $128.00 $32.00`}
                  />
                </div>
                
                <div className="flex gap-3">
                  <button onClick={() => { setShowParseModal(false); setParsedData(null); }} className="flex-1 btn-outline">
                    Cancel
                  </button>
                  <button
                    onClick={parseEOB}
                    disabled={!eobText.trim() || !selectedPatientId || parsing}
                    className="flex-1 btn-teal flex items-center justify-center gap-2"
                  >
                    {parsing ? (
                      <ArrowClockwise size={18} className="animate-spin" />
                    ) : (
                      <Sparkle size={18} />
                    )}
                    Parse with AI
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={20} className="text-green-500" />
                    <span className="font-medium text-green-800">EOB Parsed Successfully</span>
                  </div>
                  <p className="text-sm text-green-700">Review the extracted data below and import</p>
                </div>
                
                {/* Parsed Data Preview */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-slate-500">Insurance</p>
                    <p className="font-medium">{parsedData.insurance_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Policy #</p>
                    <p className="font-medium">{parsedData.policy_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">EOB Date</p>
                    <p className="font-medium">{parsedData.eob_date || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Check #</p>
                    <p className="font-medium">{parsedData.check_number || 'N/A'}</p>
                  </div>
                </div>
                
                {/* Line Items */}
                {parsedData.line_items?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Line Items</p>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left py-2 px-3">Code</th>
                            <th className="text-right py-2 px-3">Billed</th>
                            <th className="text-right py-2 px-3">Allowed</th>
                            <th className="text-right py-2 px-3">Paid</th>
                            <th className="text-right py-2 px-3">You Owe</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedData.line_items.map((item, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="py-2 px-3 font-mono">{item.procedure_code}</td>
                              <td className="py-2 px-3 text-right">{formatCurrency(item.billed_amount)}</td>
                              <td className="py-2 px-3 text-right">{formatCurrency(item.allowed_amount)}</td>
                              <td className="py-2 px-3 text-right text-green-600">{formatCurrency(item.paid_amount)}</td>
                              <td className="py-2 px-3 text-right text-amber-600">{formatCurrency(item.patient_responsibility)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Totals */}
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Total Billed</p>
                    <p className="font-bold">{formatCurrency(parsedData.total_billed)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Total Allowed</p>
                    <p className="font-bold">{formatCurrency(parsedData.total_allowed)}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600">Total Paid</p>
                    <p className="font-bold text-green-700">{formatCurrency(parsedData.total_paid)}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-xs text-amber-600">Patient Owes</p>
                    <p className="font-bold text-amber-700">{formatCurrency(parsedData.total_patient_responsibility)}</p>
                  </div>
                </div>
                
                {/* Link to Claim */}
                {claims.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Link to Claim (optional)</label>
                    <select
                      value={parsedData.claim_id || ''}
                      onChange={(e) => setParsedData(prev => ({ ...prev, claim_id: e.target.value || null }))}
                      className="input-clinical"
                    >
                      <option value="">Do not link to claim</option>
                      {claims.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.claim_number} - {c.patient_name} ({formatCurrency(c.total_charges)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <button onClick={() => setParsedData(null)} className="flex-1 btn-outline">
                    Re-Parse
                  </button>
                  <button
                    onClick={() => importEOB(parsedData)}
                    disabled={importing}
                    className="flex-1 btn-teal flex items-center justify-center gap-2"
                  >
                    {importing ? <ArrowClockwise size={18} className="animate-spin" /> : <Upload size={18} />}
                    Import EOB
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* EOB Detail Modal */}
      {selectedEOB && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">EOB Details</h2>
                <p className="text-slate-500">{selectedEOB.patient_name}</p>
              </div>
              <button onClick={() => setSelectedEOB(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            {/* EOB Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-xs text-slate-500">Insurance</p>
                <p className="font-medium">{selectedEOB.insurance_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Policy Number</p>
                <p className="font-medium">{selectedEOB.policy_number}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">EOB Date</p>
                <p className="font-medium">{formatDate(selectedEOB.eob_date)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Check #</p>
                <p className="font-medium">{selectedEOB.check_number || 'N/A'}</p>
              </div>
            </div>
            
            {/* Financial Summary */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Billed</p>
                <p className="font-bold">{formatCurrency(selectedEOB.totals?.billed)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Allowed</p>
                <p className="font-bold">{formatCurrency(selectedEOB.totals?.allowed)}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-xs text-green-600">Paid</p>
                <p className="font-bold text-green-700">{formatCurrency(selectedEOB.totals?.paid)}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-xs text-amber-600">Patient Owes</p>
                <p className="font-bold text-amber-700">{formatCurrency(selectedEOB.totals?.patient_responsibility)}</p>
              </div>
            </div>
            
            {/* Line Items */}
            {selectedEOB.line_items?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-medium text-slate-900 mb-3">Service Details</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-left py-2 px-3">Code</th>
                        <th className="text-right py-2 px-3">Billed</th>
                        <th className="text-right py-2 px-3">Allowed</th>
                        <th className="text-right py-2 px-3">Paid</th>
                        <th className="text-right py-2 px-3">You Owe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEOB.line_items.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="py-2 px-3">{item.service_date}</td>
                          <td className="py-2 px-3 font-mono">{item.procedure_code}</td>
                          <td className="py-2 px-3 text-right">{formatCurrency(item.billed_amount)}</td>
                          <td className="py-2 px-3 text-right">{formatCurrency(item.allowed_amount)}</td>
                          <td className="py-2 px-3 text-right text-green-600">{formatCurrency(item.paid_amount)}</td>
                          <td className="py-2 px-3 text-right text-amber-600">{formatCurrency(item.patient_responsibility)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            <div className="text-xs text-slate-500 mb-4">
              Imported by {selectedEOB.imported_by_name} on {formatDate(selectedEOB.imported_at)}
            </div>
            
            <button onClick={() => setSelectedEOB(null)} className="w-full btn-outline">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
